import {
  capitalize,
  getRequiredFields,
  paymentStatus,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef, useState } from "react";
import { FieldValue, useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { schema } from "./validations";

import { toast } from "@/components/Toast";
import moment from "moment";
import useApi from "./useApi";
import {
  ACCOUNT_NAME,
  APPROVED_AMT,
  BANK_ACCOUNT,
  BANK_CHARGES,
  BASELINE_DATE,
  CHECK,
  CHEQUE_NO,
  DEDUCTION_AMT,
  DOC_NO,
  DOC_TYPE,
  DOCUMENT_ID,
  POST_DOCUMENT_ID,
  DOCUMENT_TYPE,
  DRCR,
  DUE_DATE,
  HEADER_TEXT,
  INVOICE_AMT,
  INVOICE_DATE,
  INVOICE_NO,
  INVOICE_REF,
  LINE_ITEMS,
  PAY_BLOCK,
  PAYABLE_AMT,
  PAYMENT_AMOUNT,
  PAYMENT_DATE,
  PAYMENT_METHOD,
  PAYMENT_METHOD_ID,
  PAYMENT_TERMS,
  POSTING_DATE,
  POSTING_MONTH,
  POSTING_YEAR,
  RESIDUAL,
  TOTAL_PAYABLE,
  UNASSIGNED,
  VENDOR_ID,
  TARGET_VENDOR_SUBLEDGER_ID,
} from "./consts";

const useAddEditPayment = () => {
  const { paymentId, paymentAction, action, glId, month } = useParams();
  const isPosted = paymentAction == "post";
  const isPark = paymentAction == "park";
  const isFromPayment360 = !!glId;
  const navigate = useNavigate();
  const location = useLocation();
  const isOwner = location?.pathname?.includes("owner");
  const [filterCriteria, setFilterCriteria] = useState({
    [DOC_NO]: "",
    [INVOICE_NO]: "",
    invoiceDateRange: { from: undefined, to: undefined },
  });
  const [keys, setKeys] = useState({
    documentTypes: Math.random() * 2,
    vendorList: Math.random() * 2,
    paymentMethod: Math.random() * 2,
    paymentTerms: Math.random() * 2,
    drcr: Math.random() * 2,
    bankName: Math.random() * 2,
  });
  const parkAction = useRef<string>("park");
  const isView = action == "View";
  const isEdit = action == "edit";
  const [isEntryView, setIsEntryView] = useState<boolean>(true);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const form = useForm<any>({
    defaultValues: {
      [DOCUMENT_TYPE]: "",
      [DOCUMENT_ID]: "",
      [VENDOR_ID]: "",
      [BANK_ACCOUNT]: "",
      [POSTING_DATE]: moment(),
      [POSTING_YEAR]: moment().format("YYYY"),
      [POSTING_MONTH]: moment().format("MMM"),
      [ACCOUNT_NAME]: "",
      [PAYMENT_AMOUNT]: "",
      [CHEQUE_NO]: "",
      [PAYMENT_DATE]: moment(),
      [BANK_CHARGES]: "",
      [DEDUCTION_AMT]: "",
      [RESIDUAL]: "",
      [UNASSIGNED]: "",
      [TOTAL_PAYABLE]: "",
      [PAYABLE_AMT]: "",
      [LINE_ITEMS]: [
        {
          [INVOICE_DATE]: "",
          [INVOICE_NO]: "",
          [INVOICE_REF]: "",
          [DOC_NO]: "",
          [DOC_TYPE]: "",
          [HEADER_TEXT]: "",
          [BASELINE_DATE]: "",
          [DUE_DATE]: "",
          [PAY_BLOCK]: "",
          [DRCR]: "",
          [INVOICE_AMT]: "",
          [APPROVED_AMT]: "",
          [PAYABLE_AMT]: "",
          [RESIDUAL]: "",
          [TARGET_VENDOR_SUBLEDGER_ID]: ""
        },
      ],
    },
    resolver: zodResolver(schema),
  });

  const requiredFields = getRequiredFields(schema);

  const breadcrumbDataPayment = useMemo(() => {
    return paymentId
      ? [
          {
            link: `/${routes.admin}/${routes.activityDashboard}`,
            name: `Activity Dashboard - ${communityDetail?.name || ""}`,
          },
          {
            link: `/${routes.admin}/${routes.activityDashboard}/${routes.payment}/${paymentAction}`,
            name: (
              <span>
                VP
                {paymentStatus[paymentAction || ""]
                  ? " - " + paymentStatus[paymentAction || ""]?.label
                  : ""}
              </span>
            ),
          },
          {
            link: "",
            name: (
              <span className="text-primary">{`${capitalize(
                action || ""
              )}`}</span>
            ),
          },
        ]
      : [
          {
            link: `/${routes.admin}/${routes.activityDashboard}`,
            name: `Activity Dashboard - ${communityDetail?.name || ""}`,
          },
          {
            link: "",
            name: <span className="text-primary">{"Add Payment"}</span>,
          },
        ];
  }, []);

  const breadcrumbDataPaymentReport = useMemo(
    () => [
      {
        link: `/${routes.admin}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityDetail?.name || ""}`,
      },
      {
        link: `/${routes.admin}/${routes.activityDashboard}/${routes.payment}/${routes.reports}`,
        name: <span>Payment 360 Report</span>,
      },
      {
        link: `/${routes.admin}/${routes.activityDashboard}/${routes.payment}/${routes.reports}/${glId}/${month}/${routes.listing}/all`,
        name: <span>Invoice List</span>,
      },
      {
        link: "",
        name: <span className="text-primary">View Invoice</span>,
      },
    ],
    []
  );

  const breadcrumbData = isFromPayment360
    ? breadcrumbDataPaymentReport
    : breadcrumbDataPayment;

  const breadcrumbTitle = paymentId
    ? `${capitalize(action || "")} Payment - ${communityDetail?.name}`
    : `Add Payment - ${communityDetail?.name}`;

  const onBackClickPayment = () => {
    if (location?.state?.from == "invoice-listing") {
      navigate(
        `/${routes.admin}/${routes.activityDashboard}/${routes.invoice}/posted`
      );
    } else {
      paymentId
        ? navigate(
            `/${routes.admin}/${routes.activityDashboard}/${routes.payment}/${paymentAction}`
          )
        : navigate(`/${routes.admin}/${routes.activityDashboard}`);
    }
  };

  const onBackToPaymentReportList = () => {
    navigate(
      `/${routes.admin}/${routes.activityDashboard}/${routes.payment}/${routes.reports}/${glId}/${month}/${routes.listing}/all`
    );
  };

  const onBackClick = isFromPayment360
    ? onBackToPaymentReportList
    : onBackClickPayment;

  const {
    vendorList,
    isVendorLoading,
    documentTypes,
    isDocumentTypeLoading,
    openPostDialog,
    setOpenPostDialog,
    paymentMethodOptions,
    isPaymentMethodLoading,
    paymentTermOptions,
    isPaymentTermLoading,
    setVendorId,
    lineItemsInfo,
    isLineItemsLoading,
    parkPaymentInfo,
    isPaymentPending,
    glData,
    isLoadingGlMaster,
    paymentInvoiceData,
    isLoadingPaymentInvoice,
    postPaymentInfo,
    isPaymentPostPending,
    updateParkPaymentInfo,
    isUpdatePaymentPending
  } = useApi({ onBackClick, isEntryView });

  useEffect(() => {
    if (documentTypes) {
      if (!paymentId) {
        const documentTypeId = documentTypes
          ?.find((data: any) => {
            return data?.documentType?.toLowerCase() == "vp";
          })
          ?.documentTypeId?.toString();
        if (documentTypeId) {
          form.setValue(DOCUMENT_TYPE, documentTypeId);
        } else {
          toast.error(
            "The Document Type 'VP' was not found. Kindly add it to proceed."
          );
          onBackClick();
        }
      }
      setKeys((prev: any) => ({ ...prev, documentTypes: Math.random() * 4 }));
    }
  }, [documentTypes]);

  useEffect(() => {
    if (vendorList) {
      setKeys((prev: any) => ({ ...prev, vendorList: Math.random() * 4 }));
    }
  }, [vendorList]);

  //Park
  const onSubmit = (data: FieldValue<any>) => {
    if (isPark && parkAction.current == "post") {
      postPaymentInfo({
        communityId: communityDetail?.communityId,
        parkDocId: paymentId as string,
      });

      return;
    } 

    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    if (form.watch(LINE_ITEMS)?.length == 0) {
      toast.error("At least one line item is required", {
        className: "!bg-red-600 !text-white",
      });
      return;
    }

    // if (
    //   Number(form.watch(RESIDUAL)) != 0 ||
    //   Number(form.watch(UNASSIGNED)) != 0 ||
    //   Number(balanceAmount) != 0
    // ) {
    //   toast.error(
    //     "Make sure the Residual, Unassigned and Balance amounts are 0.",
    //     {
    //       className: "!bg-red-600 !text-white",
    //     }
    //   );
    //   return;
    // }

    const lineItems = (form.watch(LINE_ITEMS) as any[])
      ?.filter((item) => item[CHECK])
      .map((item) => ({
        targetId: item[DOC_NO],
        targetVendorSubledgerId: item[TARGET_VENDOR_SUBLEDGER_ID],
        originalAmount: item[APPROVED_AMT],
        payableAmount: item[PAYABLE_AMT],
        residualAmount: item[RESIDUAL]
      }));

      if (isEdit) {
        updateParkPaymentInfo({
          parkId: paymentId as string,
          communityId: communityDetail?.communityId,
          data: {
            [VENDOR_ID]: _data[VENDOR_ID],
            clearingTargetEntries: lineItems,
            [PAYMENT_METHOD]: _data[PAYMENT_METHOD_ID],
            [PAYMENT_AMOUNT]: _data[PAYMENT_AMOUNT],
            [BANK_CHARGES]: _data[BANK_CHARGES],
            [POSTING_DATE]: _data[POSTING_DATE]
              ? moment(_data[POSTING_DATE]).format("YYYY-MM-DD")
              : null,
            [POSTING_YEAR]: _data[POSTING_DATE]
              ? moment(_data[POSTING_DATE]).format("YYYY")
              : null,
            [PAYMENT_DATE]: _data[PAYMENT_DATE]
              ? moment(_data[PAYMENT_DATE]).format("YYYY-MM-DD")
              : null,
            currency: "USD",
            [BANK_ACCOUNT]: _data[BANK_ACCOUNT],
            [DOCUMENT_TYPE]: _data[DOCUMENT_TYPE],
          },
        });
      } else {
        parkPaymentInfo({
          communityId: communityDetail?.communityId,
          data: {
            [VENDOR_ID]: _data[VENDOR_ID],
            clearingTargetEntries: lineItems,
            [PAYMENT_METHOD]: _data[PAYMENT_METHOD_ID],
            [PAYMENT_AMOUNT]: _data[PAYMENT_AMOUNT],
            [BANK_CHARGES]: _data[BANK_CHARGES],
            [POSTING_DATE]: _data[POSTING_DATE]
              ? moment(_data[POSTING_DATE]).format("YYYY-MM-DD")
              : null,
            [POSTING_YEAR]: _data[POSTING_DATE]
              ? moment(_data[POSTING_DATE]).format("YYYY")
              : null,
            [PAYMENT_DATE]: _data[PAYMENT_DATE]
              ? moment(_data[PAYMENT_DATE]).format("YYYY-MM-DD")
              : null,
            currency: "USD",
            [BANK_ACCOUNT]: _data[BANK_ACCOUNT],
            [DOCUMENT_TYPE]: _data[DOCUMENT_TYPE],
          },
        });
      }
  };

  const lineItems = useWatch({ control: form.control, name: LINE_ITEMS });
  const paymentAmount = useWatch({
    control: form.control,
    name: PAYMENT_AMOUNT,
  });

  const bankCharges = useWatch({
    control: form.control,
    name: BANK_CHARGES,
  });

  const allDisabled = useMemo(() => {
    return action == "view";
  }, [isOwner, lineItems]);

  const balanceAmount = useMemo(() => {
    if (!Array.isArray(lineItems)) return 0;

    if (!isEntryView) {
      const drValues = lineItems
        .filter((item) => item[DRCR]?.toLowerCase() == "dr")
        .reduce(
          (sum, item) =>
            sum + Number(item[PAYABLE_AMT] || item.amount || 0),
          0
        );
      const crValues = lineItems
        .filter((item) => item[DRCR]?.toLowerCase() == "cr")
        .reduce(
          (sum, item) =>
            sum + Number(item[PAYABLE_AMT] || item.amount || 0),
          0
        );

      return drValues - crValues;
    }

    const crValues = lineItems
      .filter((item) => item[CHECK] && item[PAYABLE_AMT])
      .reduce((sum, item) => sum + Number(item[PAYABLE_AMT] || 0), 0);

    return Number(paymentAmount || 0) - crValues - Number(bankCharges || 0);
  }, [lineItems, bankCharges, paymentAmount, isEntryView]);

  const calculateFinalAmounts = useMemo(() => {
    if (Array.isArray(lineItems)) {
      if (lineItems.some((item: any) => item[CHECK])) {
        let totalPayable = 0,
          residual = 0;

        const payableValue = lineItems
          .filter((item) => item[CHECK] && item[PAYABLE_AMT])
          .reduce((sum, item) => sum + Number(item[PAYABLE_AMT] || 0), 0);

        const residualValue = lineItems
          .filter((item) => item[CHECK] && item[RESIDUAL])
          .reduce((sum, item) => sum + Number(item[RESIDUAL] || 0), 0);

        totalPayable = Number(payableValue) + Number(bankCharges);
        residual = Number(residualValue);

        return { totalPayable, residual };
      } else {
        return { totalPayable: 0, residual: 0 };
      }
    }
    return 0;
  }, [lineItems, bankCharges, paymentAmount]);

  useEffect(() => {
    form.setValue(TOTAL_PAYABLE, (calculateFinalAmounts as any)?.totalPayable);
    form.setValue(RESIDUAL, (calculateFinalAmounts as any)?.residual);
  }, [calculateFinalAmounts, form]);

  const onVendorChange = (selectedValue: any) => {
    const selectedOption = (vendorList || []).find(
      (vendor: any) => vendor.vendorId?.toString() === selectedValue
    );

    if (selectedOption?.paymentTerm) {
      const term = paymentTermOptions?.data
        ?.find(
          (term: any) =>
            term?.paymentTermId?.toString() ===
            selectedOption.paymentTerm?.toString()
        )
        ?.paymentTermName?.toString();
      form.setValue(PAYMENT_TERMS, term);
    }

    if (selectedOption?.paymentMethodId) {
      const method = paymentMethodOptions?.data
        ?.find(
          (method: any) =>
            method?.paymentMethodId?.toString() ===
            selectedOption.paymentMethodId?.toString()
        )
        ?.methodName?.toString();
      form.setValue(PAYMENT_METHOD, method);
      form.setValue(PAYMENT_METHOD_ID, selectedOption.paymentMethodId.toString());
    }

    setVendorId(selectedValue);
    setKeys((prev: any) => ({
      ...prev,
      paymentMethod: Math.random() * 4,
      paymentTerms: Math.random() * 4,
    }));
  };

  useEffect(() => {
    if (lineItemsInfo) {
      form.setValue(
        LINE_ITEMS,
        lineItemsInfo?.data
          ?.map((data: any) => {
            const alreadySelected = paymentInvoiceData?.data?.clearingTargetEntries?.find((doc: any) => doc.postDocumentHeaderId == data.postDocumentHeaderId);
            return {
              ...data,
              [TARGET_VENDOR_SUBLEDGER_ID]: data?.[TARGET_VENDOR_SUBLEDGER_ID]?.toString() || "",
              [INVOICE_DATE]: data?.[INVOICE_DATE]?.toString() || "",
              [INVOICE_NO]: data?.[INVOICE_NO]?.toString() || "",
              [INVOICE_REF]: data?.[INVOICE_REF]?.toString() || "",
              [DOC_NO]: data?.[DOC_NO]?.toString() || "",
              [DOC_TYPE]: data?.documentType?.toString() || "",
              [HEADER_TEXT]: data?.headerItemText?.toString() || "",
              [BASELINE_DATE]: data?.[BASELINE_DATE]?.toString() || "",
              [DUE_DATE]: data?.[DUE_DATE]?.toString() || "",
              [PAY_BLOCK]: data?.[PAY_BLOCK]?.toString() || "",
              [DRCR]: data?.[DRCR]?.toString() || "CR",
              [INVOICE_AMT]: data?.[INVOICE_AMT]?.toString() || "",
              [APPROVED_AMT]: data?.[APPROVED_AMT]?.toString() || "",
              [PAYABLE_AMT]: alreadySelected?.[PAYABLE_AMT]?.toString() || "",
              [RESIDUAL]: alreadySelected?.[RESIDUAL]?.toString() || "",
              [CHECK]: !!alreadySelected,
            }
          })
          .filter((data: any) => !data?.reversedHeader),
        { shouldValidate: true, shouldDirty: true }
      );
    }
  }, [lineItemsInfo, paymentInvoiceData]);

  useEffect(() => {
    if (paymentInvoiceData && documentTypes) {
      const documentHeaderData = paymentInvoiceData?.data?.header
      const postedLineItems = isEntryView
        ? paymentInvoiceData?.data?.clearingTargetEntries
        : [
            ...(paymentInvoiceData?.data?.lineItems || []),
          ];

      form.reset({
        [DOCUMENT_TYPE]: documentHeaderData?.[DOCUMENT_TYPE]?.toString() || "",
        [DOCUMENT_ID]:
          isPosted
            ? documentHeaderData?.[POST_DOCUMENT_ID]?.toString()
            : documentHeaderData?.[DOCUMENT_ID]?.toString() || "",
        [VENDOR_ID]: documentHeaderData?.[VENDOR_ID]?.toString() || "",
        [BANK_ACCOUNT]: documentHeaderData?.glMasterId?.toString(),
        [POSTING_DATE]: documentHeaderData?.[POSTING_DATE]?.toString() || "",
        [POSTING_MONTH]: documentHeaderData?.[POSTING_DATE]
          ? moment(documentHeaderData?.[POSTING_DATE]).format("MMM")
          : "",
        [POSTING_YEAR]: documentHeaderData?.[POSTING_DATE]
          ? moment(documentHeaderData?.[POSTING_DATE]).format("YYYY")
          : "",
        [ACCOUNT_NAME]: documentHeaderData?.accountDescription?.toString() || "",
        [PAYMENT_AMOUNT]: documentHeaderData?.approvedAmount?.toString() || "",
        [PAYABLE_AMT]: documentHeaderData?.approvedAmount?.toString() || "",
        [CHEQUE_NO]: documentHeaderData?.[CHEQUE_NO]?.toString() || "",
        [PAYMENT_DATE]: documentHeaderData?.invoiceDate?.toString() || "",
        [BANK_CHARGES]: documentHeaderData?.[BANK_CHARGES]?.toString() || "",
        [DEDUCTION_AMT]: documentHeaderData?.[DEDUCTION_AMT]?.toString() || "",
        ...(isPosted ?
          {
            [LINE_ITEMS]: postedLineItems
              ?.map((data: any) => ({
                ...data,
                [TARGET_VENDOR_SUBLEDGER_ID]: data?.[TARGET_VENDOR_SUBLEDGER_ID]?.toString() || "",
                [INVOICE_DATE]: data?.[INVOICE_DATE]?.toString() || "",
                [INVOICE_NO]: data?.[INVOICE_NO]?.toString() || "",
                [INVOICE_REF]: data?.[INVOICE_REF]?.toString() || "",
                [DOC_NO]: data?.[DOC_NO]?.toString() || data?.targetId?.toString() || "",
                [DOC_TYPE]: data?.[DOC_TYPE]?.toString() || "",
                [HEADER_TEXT]: data?.[HEADER_TEXT]?.toString() || data?.headerComments?.toString() || data?.itemText?.toString() || "",
                [BASELINE_DATE]: data?.[BASELINE_DATE]?.toString() || "",
                [DUE_DATE]: data?.[DUE_DATE]?.toString() || "",
                [PAY_BLOCK]: data?.[PAY_BLOCK]?.toString() || "",
                [DRCR]: data?.[DRCR]?.toString() || "CR",
                [INVOICE_AMT]: data?.[INVOICE_AMT]?.toString() || data?.originalAmount?.toString() || data?.amount?.toString() || "",
                [APPROVED_AMT]: data?.[APPROVED_AMT]?.toString() || data?.amount?.toString() || "",
                [PAYABLE_AMT]: data?.[PAYABLE_AMT]?.toString() || data?.amount?.toString() || "",
                [RESIDUAL]: data?.[RESIDUAL]?.toString() || "",
                glAccountNumber: data?.glAccountNumber?.toString() || data?.glMasterName?.toString() || "",
                numberOfUnits: data?.numberOfUnits?.toString() || "",
                ratePerUnit: data?.ratePerUnit?.toString() || "",
                amount: data?.amount?.toString() || data?.payableAmount?.toString() || data?.originalAmount?.toString() || "",
                [CHECK]: true,
              }))
          } : {})
      });

      onVendorChange(documentHeaderData?.[VENDOR_ID]?.toString());
      setKeys((prev: any) => ({
        ...prev,
        vendorList: Math.random() * 4,
        documentTypes: Math.random() * 4,
      }));
    }
  }, [paymentInvoiceData, documentTypes, isEntryView]);

  const onChangeFilterCriteria = (key: string, value: string) => {
    setFilterCriteria((prev) => ({ ...prev, [key]: value }));
  };

  const onApplyFilter = () => {
    const lineItems = [...form.watch(LINE_ITEMS)]?.filter((item: any) => {
      return (
        item[DOC_NO] == filterCriteria[DOC_NO] ||
        item[INVOICE_NO] == filterCriteria[INVOICE_NO] ||
        (moment(item[INVOICE_DATE]).isAfter(
          filterCriteria.invoiceDateRange.from
        ) &&
          moment(item[INVOICE_DATE]).isBefore(
            filterCriteria.invoiceDateRange.to
          ))
      );
    });

    form.setValue(LINE_ITEMS, lineItems);
  };

  const onClearFilter = () => {
    setFilterCriteria({
      [DOC_NO]: "",
      [INVOICE_NO]: "",
      invoiceDateRange: { from: undefined, to: undefined },
    });

    form.setValue(
      LINE_ITEMS,
      paymentInvoiceData?.data?.map((data: any) => ({
        ...data,
        [TARGET_VENDOR_SUBLEDGER_ID]: data?.[TARGET_VENDOR_SUBLEDGER_ID]?.toString() || "",
        [INVOICE_DATE]: data?.[INVOICE_DATE]?.toString() || "",
        [INVOICE_NO]: data?.[INVOICE_NO]?.toString() || "",
        [INVOICE_REF]: data?.[INVOICE_REF]?.toString() || "",
        [DOC_NO]: data?.[DOC_NO]?.toString() || "",
        [DOC_TYPE]: data?.[DOC_TYPE]?.toString() || "",
        [HEADER_TEXT]: data?.[HEADER_TEXT]?.toString() || "",
        [BASELINE_DATE]: data?.[BASELINE_DATE]?.toString() || "",
        [DUE_DATE]: data?.[DUE_DATE]?.toString() || "",
        [PAY_BLOCK]: data?.[PAY_BLOCK]?.toString() || "",
        [DRCR]: data?.[DRCR]?.toString() || "CR",
        [INVOICE_AMT]: data?.[INVOICE_AMT]?.toString() || "",
        [APPROVED_AMT]: data?.[APPROVED_AMT]?.toString() || "",
        [PAYABLE_AMT]: data?.[PAYABLE_AMT]?.toString() || "",
        [RESIDUAL]: data?.[RESIDUAL]?.toString() || "",
        [CHECK]: false,
      })),
      { shouldValidate: true, shouldDirty: true }
    );
  };

  const bankAccount = useWatch({ control: form.control, name: BANK_ACCOUNT });
  
  useEffect(() => {
    const BANK_ACC = bankAccount;

    if (!isView && glData && !BANK_ACC) {
      const accDtl = glData?.data?.find((data: any) => data?.glAccountNumber == "1001");
      form.setValue(BANK_ACCOUNT, accDtl?.glMasterId?.toString() || "");
      form.setValue(ACCOUNT_NAME, accDtl?.accountShortDesc?.toString() || "");
      setKeys((prev: any) => ({
        ...prev,
        bankName: Math.random() * 4,
      }));
    }
  }, [glData]);

  const documentId = useWatch({ control: form.control, name: DOCUMENT_ID });

  return {
    breadcrumbData,
    onBackClick,
    form,
    onSubmit,
    requiredFields,
    balanceAmount,
    vendorList,
    isVendorLoading,
    onCancel: onBackClick,
    breadcrumbTitle,
    isDocumentTypeLoading,
    documentTypes,
    keys,
    allDisabled,
    openPostDialog,
    setOpenPostDialog,
    paymentMethodOptions,
    isPaymentMethodLoading,
    paymentTermOptions,
    isPaymentTermLoading,
    onVendorChange,
    isLineItemsLoading,
    glData,
    isLoadingGlMaster,
    filterCriteria,
    onChangeFilterCriteria,
    onApplyFilter,
    onClearFilter,
    paymentId,
    documentId,
    isLoadingPaymentInvoice,
    isPosted,
    isPark,
    isPaymentPending: isUpdatePaymentPending || isPaymentPending,
    isPaymentPostPending,
    isEdit,
    parkAction,
    isView,
    isEntryView,
    setIsEntryView
  };
};

export default useAddEditPayment;
